import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Pagination } from "@mui/material";
import { toast } from "react-toastify";
import ProductCard from "../Cards/ProductCard";
import ViewModal from "../Modal";

const PopularCategories = ({
  addToCartProduct,
  addToWishListProduct,
  addToCompareListProduct,
  ...props
}) => {
  const [popularcategories, setPopularcategories] = useState([]);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 8,
    totalRecords: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [open, setOpen] = useState(false);
  const [state, setState] = useState({});
  const sectionRef = useRef(null);

  const handleClose = () => setOpen(false);
  const handleClickOpen = (item) => {
    setOpen(true);
    setState(item);
  };

  const handlePageChange = (event, value) => {
    setFilter((prev) => ({ ...prev, page: value }));
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const fetchPopularcategories = () => {
    setLoading(true);
    if (error) setError(false);
    axios
      .get("get-products", {
        params: { popularcategories: true, ...filter },
      })
      .then((response) => {
        const data = response?.data?.data || [];
        setPopularcategories(data.map((el) => ({ ...el, id: el._id })));
        setFilter((prev) => ({
          ...prev,
          totalRecords: response?.data?.totalRecords,
        }));
      })
      .catch((err) => {
        setPopularcategories([]);
        setError(true);
        if (err?.response?.data?.message) {
          toast.error(err.response.data.message);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPopularcategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.page]);

  const totalRecords = Number(filter?.totalRecords) || 0;
  const limit = Number(filter?.limit) || 8;
  const pageCount = Math.max(1, Math.ceil(totalRecords / limit));

  return (
    <section className="gppc-section" ref={sectionRef}>
      <div className="gppc-bg-orb gppc-bg-orb-tr" />
      <div className="gppc-bg-orb gppc-bg-orb-bl" />
      <div className="gppc-bg-grid" aria-hidden="true" />

      <div className="gppc-inner">
        {/* HEADER */}
        <div className="gppc-head">
          <div className="gppc-head-text">
            <div className="gppc-eyebrow">
              <span className="gppc-eyebrow-dot" />
              MOST LOVED · TRENDING NOW
            </div>
            <h2 className="gppc-title">
              Popular <span className="gppc-title-accent">Categories</span>
            </h2>
            <p className="gppc-sub">
              The gift cards our customers shop most. Curated favourites across
              gaming, dining, entertainment and lifestyle.
            </p>
          </div>

          <div className="gppc-head-right">
            {!loading && !error && totalRecords > 0 && (
              <div className="gppc-meta-pill">
                <strong>{totalRecords}</strong> picks
              </div>
            )}
            <Link to="/shop" className="gppc-viewall">
              View all
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
          </div>
        </div>

        {/* GRID */}
        {loading ? (
          <div className="gppc-grid">
            {Array.from({ length: filter?.limit || 8 }).map((_, i) => (
              <div key={i} className="gppc-skel">
                <div className="gppc-skel-img" />
                <div className="gppc-skel-line gppc-skel-line-lg" />
                <div className="gppc-skel-line gppc-skel-line-md" />
                <div className="gppc-skel-line gppc-skel-line-sm" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="gppc-empty">
            <div className="gppc-empty-icon" aria-hidden="true">⚠</div>
            <h3>Couldn't load popular categories</h3>
            <p>Please check your connection and try again.</p>
            <button
              type="button"
              className="gppc-empty-btn"
              onClick={fetchPopularcategories}
            >
              Try again
            </button>
          </div>
        ) : popularcategories.length === 0 ? (
          <div className="gppc-empty">
            <div className="gppc-empty-icon" aria-hidden="true">🔥</div>
            <h3>Nothing trending yet</h3>
            <p>Check back soon — popular picks update daily.</p>
            <Link to="/shop" className="gppc-empty-btn">
              Browse all gift cards
            </Link>
          </div>
        ) : (
          <>
            <div className="gppc-grid">
              {popularcategories.map((product, index) => (
                <div
                  key={product?.id || index}
                  className="gppc-card-wrap"
                  style={{ animationDelay: `${Math.min(index * 60, 480)}ms` }}
                >
                  <div className="gppc-card-glow" aria-hidden="true" />
                  <ProductCard
                    product={product}
                    addToWishListProduct={addToWishListProduct}
                    addToCartProduct={addToCartProduct}
                    handleClickOpen={handleClickOpen}
                    carts={props.carts}
                    {...props}
                  />
                </div>
              ))}
            </div>

            {pageCount > 1 && (
              <div className="gppc-pagination">
                <Pagination
                  count={pageCount}
                  size="medium"
                  page={filter?.page || 1}
                  onChange={handlePageChange}
                  siblingCount={1}
                  shape="rounded"
                />
              </div>
            )}
          </>
        )}
      </div>

      <ViewModal
        addToCartProduct={addToCartProduct}
        open={open}
        onClose={handleClose}
        product={state}
        carts={props.carts}
        {...props}
      />

      <style>{`
        /* ============ SECTION ============ */
        .gppc-section {
          position: relative;
          background: linear-gradient(180deg, #07060a 0%, #0b0b0c 100%);
          padding: 70px 22px 80px;
          overflow: hidden;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          color: #fff;
          border-top: 1px solid rgba(212, 168, 87, 0.08);
        }
        .gppc-bg-orb {
          position: absolute;
          width: 380px;
          height: 380px;
          border-radius: 50%;
          filter: blur(120px);
          pointer-events: none;
          opacity: 0.55;
        }
        .gppc-bg-orb-tr {
          background: rgba(212, 168, 87, 0.18);
          top: -140px;
          right: -100px;
        }
        .gppc-bg-orb-bl {
          background: rgba(212, 168, 87, 0.12);
          bottom: -160px;
          left: -100px;
        }
        .gppc-bg-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(212, 168, 87, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(212, 168, 87, 0.04) 1px, transparent 1px);
          background-size: 48px 48px;
          mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
          -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
          pointer-events: none;
          opacity: 0.5;
        }

        .gppc-inner {
          position: relative;
          max-width: 1300px;
          margin: 0 auto;
          z-index: 1;
        }

        /* ============ HEADER ============ */
        .gppc-head {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 18px;
          margin-bottom: 36px;
          flex-wrap: wrap;
        }
        .gppc-head-text { max-width: 720px; }
        .gppc-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px;
          background: rgba(212, 168, 87, 0.1);
          border: 1px solid rgba(212, 168, 87, 0.3);
          color: #d4a857;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 2px;
          margin-bottom: 14px;
        }
        .gppc-eyebrow-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #d4a857;
          box-shadow: 0 0 10px #d4a857;
          animation: gppc-pulse 1.6s ease-in-out infinite;
        }
        @keyframes gppc-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(0.85); }
        }
        .gppc-title {
          font-family: "Playfair Display", "Times New Roman", serif;
          font-size: 38px;
          font-weight: 700;
          color: #fff;
          margin: 0 0 8px;
          line-height: 1.15;
          letter-spacing: -0.4px;
        }
        .gppc-title-accent {
          background: linear-gradient(135deg, #f1cf85, #b58a3f);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          font-style: italic;
          font-weight: 600;
        }
        .gppc-sub {
          font-size: 14.5px;
          color: #9aa0a8;
          line-height: 1.55;
          margin: 0;
          max-width: 580px;
        }

        .gppc-head-right {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        .gppc-meta-pill {
          padding: 8px 14px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(212, 168, 87, 0.2);
          color: #cfd2d6;
          border-radius: 999px;
          font-size: 12px;
        }
        .gppc-meta-pill strong { color: #d4a857; font-weight: 700; margin-right: 4px; }

        .gppc-viewall {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 11px 18px;
          background: linear-gradient(135deg, #f1cf85 0%, #d4a857 50%, #b58a3f 100%);
          color: #1a1208 !important;
          border: 1px solid rgba(255, 232, 178, 0.5);
          border-radius: 999px;
          font-size: 12.5px;
          font-weight: 700;
          letter-spacing: 0.6px;
          text-decoration: none;
          box-shadow: 0 6px 16px rgba(212, 168, 87, 0.3);
          transition: transform .25s ease, box-shadow .25s ease, filter .25s ease;
        }
        .gppc-viewall:hover {
          color: #1a1208 !important;
          text-decoration: none;
          transform: translateY(-1px);
          filter: brightness(1.05);
          box-shadow: 0 10px 22px rgba(212, 168, 87, 0.45);
        }
        .gppc-viewall svg { transition: transform .2s ease; }
        .gppc-viewall:hover svg { transform: translateX(2px); }

        /* ============ GRID ============ */
        .gppc-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 22px;
        }
        .gppc-card-wrap {
          position: relative;
          opacity: 0;
          transform: translateY(14px);
          animation: gppc-fadeup .55s cubic-bezier(0.22, 1, 0.36, 1) forwards;
          transition: transform .35s cubic-bezier(0.22, 1, 0.36, 1);
          border-radius: 16px;
        }
        .gppc-card-wrap:hover { transform: translateY(-6px); }
        @keyframes gppc-fadeup {
          to { opacity: 1; transform: translateY(0); }
        }
        .gppc-card-glow {
          position: absolute;
          inset: -2px;
          border-radius: 18px;
          background: linear-gradient(135deg, rgba(212, 168, 87, 0.6), rgba(180, 130, 40, 0));
          opacity: 0;
          transition: opacity .3s ease;
          pointer-events: none;
          z-index: 0;
          filter: blur(8px);
        }
        .gppc-card-wrap:hover .gppc-card-glow { opacity: 0.55; }

        /* ----- DARK CARD STYLING (overrides on the inner ProductCard) ----- */
        .gppc-card-wrap > .col-xl-12,
        .gppc-card-wrap > [class*="col-"] {
          padding: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
          flex: 0 0 100% !important;
          height: auto !important;
        }
        .gppc-card-wrap .product-item {
          position: relative;
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%) !important;
          border: 1px solid rgba(212, 168, 87, 0.18) !important;
          border-radius: 16px !important;
          overflow: hidden !important;
          box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35) !important;
          transition: border-color .3s ease, box-shadow .3s ease !important;
          z-index: 1;
          height: 100%;
        }
        .gppc-card-wrap:hover .product-item {
          border-color: rgba(212, 168, 87, 0.55) !important;
          box-shadow: 0 18px 38px rgba(212, 168, 87, 0.18), 0 6px 14px rgba(0, 0, 0, 0.4) !important;
        }
        .gppc-card-wrap .product-image {
          background: #0b0b0c !important;
          padding: 14px !important;
          border-radius: 14px 14px 0 0 !important;
          position: relative;
          overflow: hidden;
        }
        .gppc-card-wrap .product-image img {
          transition: transform .5s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .gppc-card-wrap:hover .product-image img {
          transform: scale(1.04);
        }
        .gppc-card-wrap .product-info {
          padding: 14px 14px 16px !important;
          background: transparent !important;
        }
        .gppc-card-wrap .product-info h2,
        .gppc-card-wrap .product-card-title h2 {
          color: #fff !important;
        }
        .gppc-card-wrap .product-info h2 a,
        .gppc-card-wrap .product-card-title a {
          color: #fff !important;
        }
        .gppc-card-wrap .product-info h2 a:hover,
        .gppc-card-wrap .product-card-title a:hover {
          color: #d4a857 !important;
        }
        .gppc-card-wrap .product-info h6 {
          color: #9aa0a8 !important;
        }
        .gppc-card-wrap .product-info .present-price,
        .gppc-card-wrap .present-price {
          color: #d4a857 !important;
          font-weight: 700;
        }
        .gppc-card-wrap .product-info .old-price,
        .gppc-card-wrap .old-price {
          color: #6b7280 !important;
        }
        .gppc-card-wrap .tag-wrap .tag {
          background: linear-gradient(135deg, #d4a857, #b58a3f) !important;
          color: #1a1208 !important;
          border: none !important;
          font-weight: 700 !important;
          font-size: 10px !important;
          letter-spacing: 0.4px;
          padding: 4px 10px !important;
          border-radius: 999px !important;
          box-shadow: 0 4px 10px rgba(212, 168, 87, 0.3) !important;
        }
        .gppc-card-wrap .info-wrap button {
          background: rgba(15, 15, 16, 0.85) !important;
          border: 1px solid rgba(212, 168, 87, 0.3) !important;
          color: #d4a857 !important;
          backdrop-filter: blur(6px);
        }
        .gppc-card-wrap .info-wrap button:hover {
          background: linear-gradient(135deg, #d4a857, #b58a3f) !important;
          color: #1a1208 !important;
          border-color: transparent !important;
        }

        /* ============ SKELETON ============ */
        .gppc-skel {
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px solid rgba(212, 168, 87, 0.12);
          border-radius: 16px;
          padding: 14px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .gppc-skel-img {
          height: 200px;
          border-radius: 10px;
          background: linear-gradient(90deg, #1a1208 0%, #2a1f10 50%, #1a1208 100%);
          background-size: 200% 100%;
          animation: gppc-shimmer 1.4s linear infinite;
        }
        .gppc-skel-line {
          height: 12px;
          border-radius: 4px;
          background: linear-gradient(90deg, #1a1208 0%, #2a1f10 50%, #1a1208 100%);
          background-size: 200% 100%;
          animation: gppc-shimmer 1.4s linear infinite;
        }
        .gppc-skel-line-lg { height: 16px; width: 80%; }
        .gppc-skel-line-md { width: 55%; }
        .gppc-skel-line-sm { width: 35%; }
        @keyframes gppc-shimmer {
          from { background-position: 200% 0; }
          to { background-position: -200% 0; }
        }

        /* ============ EMPTY / ERROR ============ */
        .gppc-empty {
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px dashed rgba(212, 168, 87, 0.3);
          border-radius: 16px;
          padding: 56px 24px;
          text-align: center;
          max-width: 540px;
          margin: 0 auto;
        }
        .gppc-empty-icon {
          font-size: 36px;
          color: #d4a857;
          margin-bottom: 12px;
        }
        .gppc-empty h3 {
          font-size: 18px;
          color: #fff;
          margin: 0 0 6px;
          font-weight: 700;
        }
        .gppc-empty p {
          color: #9aa0a8;
          margin: 0 0 20px;
          font-size: 13.5px;
        }
        .gppc-empty-btn {
          display: inline-flex;
          align-items: center;
          background: linear-gradient(135deg, #d4a857, #b58a3f);
          color: #1a1208 !important;
          border: none;
          padding: 11px 22px;
          border-radius: 10px;
          font-weight: 700;
          font-size: 13px;
          letter-spacing: 0.5px;
          cursor: pointer;
          text-decoration: none;
          transition: transform .2s ease, filter .2s ease;
          font-family: inherit;
        }
        .gppc-empty-btn:hover {
          transform: translateY(-2px);
          filter: brightness(1.05);
          color: #1a1208 !important;
          text-decoration: none;
        }

        /* ============ PAGINATION ============ */
        .gppc-pagination {
          display: flex;
          justify-content: center;
          padding: 40px 0 0;
        }
        .gppc-pagination .MuiPaginationItem-root {
          color: #cfd2d6 !important;
          font-weight: 600;
          border-radius: 10px;
          margin: 0 2px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(212, 168, 87, 0.15);
        }
        .gppc-pagination .MuiPaginationItem-root:hover {
          background: rgba(212, 168, 87, 0.15) !important;
          color: #d4a857 !important;
          border-color: rgba(212, 168, 87, 0.4);
        }
        .gppc-pagination .MuiPaginationItem-root.Mui-selected {
          background: linear-gradient(135deg, #d4a857, #b58a3f) !important;
          color: #1a1208 !important;
          border-color: transparent !important;
          box-shadow: 0 6px 14px rgba(212, 168, 87, 0.3);
        }
        .gppc-pagination .MuiPaginationItem-root.Mui-selected:hover {
          background: linear-gradient(135deg, #f1cf85, #d4a857) !important;
        }

        /* ============ RESPONSIVE ============ */
        @media (max-width: 1100px) {
          .gppc-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        @media (max-width: 820px) {
          .gppc-section { padding: 56px 18px 64px; }
          .gppc-title { font-size: 28px; }
          .gppc-sub { font-size: 13.5px; }
          .gppc-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
          .gppc-skel-img { height: 160px; }
        }
        @media (max-width: 480px) {
          .gppc-section { padding: 44px 14px 52px; }
          .gppc-title { font-size: 22px; }
          .gppc-eyebrow { font-size: 10px; letter-spacing: 1.5px; }
          .gppc-grid { grid-template-columns: 1fr; }
          .gppc-head { gap: 14px; flex-direction: column; align-items: flex-start; }
          .gppc-head-right { width: 100%; }
          .gppc-viewall { flex: 1; justify-content: center; }
          .gppc-skel-img { height: 200px; }
        }
      `}</style>
    </section>
  );
};

export default PopularCategories;
